import React from 'react';
import ReactDomServer from 'react-dom/server';
import { getDefaultAgendaImage } from '../../../../store/utils/create-event';
import { TemplateClassNames } from '../../../../types/template-layouts';

import { ColorOptions, EmailTypeEnum, FontPack, LanguagesAbbr, Channel, Templates, BrandliveEvent, PageModuleType, Session, IScheduledEmailContent, IScheduledEmailCustomizations, IScheduledEmailSettings, BLAdmin, SessionTypesEnum } from '../../../../types/working-model';
import { getStartTime } from '../../../../utils/get-event-start-time';
import { convertHtmlString, getPrefixUrl, isRtlLanguage, niceDateTimeTzEmail } from '../../../../utils/utils';
import { EmailWrapper } from './event-email-base';
import { EventEmail, EmailContent, EmailTextPhrases, EmailCalendarLinks } from './event-emails';
import { googleCalendarLink, icsFile } from '../../../../utils/calendar-invites';
import { UploadFile } from '../../../../connection/uploads';

// complete email packaged as string to insert in db and pass on to email sender
export const getEmailToSend = async (
	user: BLAdmin,
	token: string,
	emailType: EmailTypeEnum,
	workingEvent: BrandliveEvent,
	currentChannel: Channel,
	publishedUrl: string,
	emailContent: IScheduledEmailCustomizations,
	language?: LanguagesAbbr,
	sessionUUID?: string,
	emailSendTime?: number,
	timezone = 'America/Los_Angeles',
	emailSettings?: IScheduledEmailSettings,
): Promise<IScheduledEmailCustomizations> => {
	const colors: ColorOptions = workingEvent.settings.email?.colors?.colors ?? workingEvent.settings.design.colors.colors;
	const fonts: FontPack = workingEvent.settings.design.font;
	const session = sessionUUID ? workingEvent?.sessions.find(session => session.uuid === sessionUUID) : undefined;
	const NameFirstInSubject = [EmailTypeEnum.session_reminder, EmailTypeEnum.session_live, EmailTypeEnum.session_ended];

	const emailLanguages = (): LanguagesAbbr[] => {
		if (language) {
			return [language];
		} else if (session) {
			return session.languages as LanguagesAbbr[];
		}
		return Array.from(new Set(workingEvent.sessions.map(session => session.languages as LanguagesAbbr[]).flat()));
	};

	const isAllOnDemand = () => {
		return !workingEvent.sessions.find(session => session.session_type !== SessionTypesEnum.onDemand);
	};

	const isOnDemandSession = () => {
		return session?.session_type === SessionTypesEnum.onDemand;
	};

	await Promise.all(emailLanguages().map(async (language) => {
		const translations = await import(`../../../../i18n/${language}/email.json`);
		const calendarLinks = emailSettings?.showAddCalendar && !isAllOnDemand()
			? await getCalendarLinks(user, token, workingEvent, currentChannel, publishedUrl, language, translations, session)
			: undefined;

		const jsx = (
			<ScheduledEmail
				emailType={emailType}
				language={language}
				workingEvent={workingEvent}
				currentChannel={currentChannel}
				publishedUrl={publishedUrl}
				emailTranslation={translations}
				sessionUUID={sessionUUID}
				emailSendTime={emailSendTime}
				emailToSend={true}
				customContent={emailContent[language]}
				customSettings={emailSettings}
				calendarLinks={calendarLinks}
				timezone={timezone}
				hideCalendar={isAllOnDemand() || isOnDemandSession()}
			/>
		);
		const html = ReactDomServer.renderToString(jsx);
		const emailBody = EmailWrapper(html, colors.secondaryBackgroundColor[0], fonts);

		const { name } = session ? sessionNameAndDescription(session, language) : eventNameAndDescription(workingEvent, language);


		const subject = emailContent[language]?.subject ??
			(NameFirstInSubject.includes(emailType) ?
				`${name} ${translations[`subject.${emailType}`]}`
				: `${translations[`subject.${emailType}`]} ${name}`);

		emailContent[language] = {
			...emailContent[language],
			base: emailBody,
			subject: subject,
		};
	}));

	return emailContent;
};


export interface ScheduledEmailProps {
	emailType: EmailTypeEnum,
	language: LanguagesAbbr,
	workingEvent: BrandliveEvent,
	currentChannel: Channel,
	publishedUrl: string,
	emailTranslation: EmailTextPhrases;
	sessionUUID?: string,
	emailSendTime?: number;
	/** if we are fetching the scheduled email to send rather than display
	certain elements will be handled differently */
	emailToSend?: boolean;
	customContent?: IScheduledEmailContent;
	customSettings?: IScheduledEmailSettings;
	calendarLinks?: EmailCalendarLinks;
	onEdit?: (value: string | IScheduledEmailContent["session_image"], key: string) => void;
	timezone?: string;
	idx?: string;
	sent?: boolean;
	hideCalendar?: boolean;
}

// email body to display in editor
export const ScheduledEmail = ({
	emailType,
	language,
	workingEvent,
	currentChannel,
	publishedUrl,
	emailTranslation,
	sessionUUID,
	emailSendTime,
	emailToSend = false,
	customContent,
	customSettings,
	calendarLinks,
	onEdit,
	timezone = 'America/Los_Angeles',
	idx,
	sent,
	hideCalendar,
}: ScheduledEmailProps): JSX.Element => {
	const session = sessionUUID ? workingEvent?.sessions.find(session => session.uuid === sessionUUID) : undefined;
	if (sessionUUID && !session) return <></>;

	const template: Templates = workingEvent.template.template;
	const colors: ColorOptions = workingEvent.settings.email?.colors?.colors ?? workingEvent.settings.design.colors.colors;
	const fonts: FontPack = workingEvent.settings.design.font;
	const hideBrandliveLogo = workingEvent.settings.email?.hideBrandliveLogo ?? false;
	const clientLogo: string = currentChannel.logos.color ?? currentChannel.logos.white ?? currentChannel.logos.gray ?? '';

	const content = emailType.includes('session') && session ?
		getSessionEmailContent(emailType as EmailTypeEnum, language, workingEvent, currentChannel, publishedUrl, session, emailSendTime, timezone)
		: getEventEmailContent(emailType as EmailTypeEnum, language, workingEvent, currentChannel, publishedUrl, timezone);

	const emailContent = { ...content, translations: emailTranslation, settings: customSettings, calendarLinks };

	const forceRtl = isRtlLanguage(language);

	return EventEmail({
		emailType: emailType as EmailTypeEnum,
		template,
		colors,
		fonts,
		hideBrandliveLogo,
		clientLogo,
		emailContent,
		language,
		emailToSend,
		customContent,
		onEdit,
		idx,
		sent,
		forceRtl,
		hideCalendar,
	});
};

const getEventEmailContent = (
	emailType: EmailTypeEnum,
	language: LanguagesAbbr,
	workingEvent: BrandliveEvent,
	currentChannel: Channel,
	publishedUrl: string,
	timezone = 'America/Los_Angeles',
): Omit<EmailContent, "translations"> => {
	const { name, description, image: bannerImage } = eventNameAndDescription(workingEvent, language);

	const eventStart = getStartTime(workingEvent, false);
	const start: string | null = eventStart ? niceDateTimeTzEmail(eventStart as number, language, timezone) : null;

	const registrationRequired = workingEvent.registration_on;

	const url = getUrl(currentChannel, workingEvent, publishedUrl, language);

	const image = bannerImage ?? getDefaultAgendaImage(TemplateClassNames[workingEvent.template.name], 0);

	return {
		event: {
			name: convertHtmlString(name),
			description: convertHtmlString(description),
			start,
			registrationRequired,
			url,
			image,
		}
	};
};

const getSessionEmailContent = (
	emailType: EmailTypeEnum,
	language: LanguagesAbbr,
	workingEvent: BrandliveEvent,
	currentChannel: Channel,
	publishedUrl: string,
	session: Session,
	emailSendTime: number | undefined,
	timezone = 'America/Los_Angeles',
): Omit<EmailContent, "translations"> => {
	const { name, description } = sessionNameAndDescription(session, language);

	const url = getUrl(currentChannel, workingEvent, publishedUrl, language, session.uuid);

	// We want the findIndex because it'll keep the default image consistent across renders. Once the image (default or uploaded) is saved, the default function will stop executing.
	const image = session.image ?? session.pre_broadcast_thumbnail ?? getDefaultAgendaImage(TemplateClassNames[workingEvent.template.name], workingEvent.sessions.findIndex(_session => _session.uuid === session.uuid));

	const start: string | null = session.timestamp ? niceDateTimeTzEmail(session.timestamp, language, timezone) : null;
	const oneDay = 24 * 60 * 60 * 1000;
	const daysToStart = emailType === EmailTypeEnum.session_reminder && session.timestamp && emailSendTime ?
		Math.floor(session.timestamp.valueOf() / oneDay) - Math.floor(emailSendTime.valueOf() / oneDay)
		: undefined;

	return {
		session: {
			name,
			description: description,
			url,
			image,
			start,
			daysToStart,
		},
	};
};

export const eventNameAndDescription = (event: BrandliveEvent, language: LanguagesAbbr): { name: string, description: string; image: string; } => {
	const mainBanner = event.homepage && event.homepage.modules.find(module => module.type === PageModuleType.main_event_banner);
	const name = mainBanner?.content?.title?.[language] ?? event.registration_settings?.title?.[language] ?? event.registration_settings?.title?.base ?? '';
	const description = mainBanner?.content?.description?.[language] ?? mainBanner?.content?.description?.base ?? '';
	const image = mainBanner?.content?.image ?? mainBanner?.content?.originalImage;

	return { name: convertHtmlString(name), description: convertHtmlString(description), image };
};

export const sessionNameAndDescription = (session: Session, language: LanguagesAbbr): { name: string, description: string; } => {
	const descriptionModule = session.modules.find(module => module.type === PageModuleType.description);
	const name = descriptionModule?.content?.title?.[language] ?? descriptionModule?.content?.title?.base ?? '';
	const description = descriptionModule?.content?.description?.[language] ?? descriptionModule?.content?.description?.base ?? '';

	return { name: convertHtmlString(name), description: convertHtmlString(description) };
};

const getUrl = (channel: Channel, workingEvent: BrandliveEvent, publishedUrl: string, language: LanguagesAbbr, sessionUUID?: string) => {
	const isDev = process.env.NODE_ENV === 'development';
	const basePath = isDev ? 'http://localhost:3000' : getPrefixUrl(channel.url);
	const eventPath = isDev ? `event/${workingEvent.uuid}` : publishedUrl;

	if (sessionUUID) {
		return `${basePath}/${eventPath}/${language}/session/${sessionUUID}`;
	}
	return `${basePath}/${eventPath}/${language}`;
};

const getCalendarLinks = async (
	user: BLAdmin,
	token: string,
	event: BrandliveEvent,
	channel: Channel,
	publishedUrl: string,
	language: LanguagesAbbr,
	translations: EmailTextPhrases,
	session?: Session,
): Promise<EmailCalendarLinks | undefined> => {
	const start = session?.timestamp ?? event.sessions.map(session => session.timestamp).filter(timestamp => timestamp).sort()[0];
	const end = session?.end_timestamp ?? event.sessions.map(session => session.end_timestamp).filter(timestamp => timestamp).sort((a, b) => (b as number) - (a as number))[0];
	const { name, description } = session ? sessionNameAndDescription(session, language) : eventNameAndDescription(event, language);
	const url = getUrl(channel, event, publishedUrl, language, session?.uuid);
	const uuid = session?.uuid ?? event.uuid;

	try {
		const file = icsFile(start, end, name, description, url, uuid, translations);
		const icsLink = file ? await UploadFile(user, token, file, undefined, { 'batch-id': `get_calendar_links_${event.event}` }) : '';
		const calendarLink = googleCalendarLink(start, end, name, description, url, translations);

		return { iCal: icsLink, outlook: icsLink, google: calendarLink };
	} catch (e) {
		console.error("error getting calendar links: ", e);
		return undefined;
	}
};